import { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../config';

export interface NextMatch {
  id: string;
  location: string;
  date: string;
  time: string;
  playersJoined: number;
  maxPlayers: number;
  status: string;
  creatorId?: string;
  players?: Array<{ userId?: string }>;
}

export interface AvailableMatch {
  id: string;
  location: string;
  date: string;
  time: string;
  playersJoined: number;
  maxPlayers: number;
  status: string;
  organizerName: string;
  friendsJoined?: string[];
  context?: {
    isKnownOrganizer?: boolean;
    isKnownLocation?: boolean;
    timesPlayedAtLocation?: number;
    friendsInMatchCount?: number;
  };
  relevanceScore?: number;
  badge?: { type: string; label: string; priority: number; color?: string };
  isFromGroup?: boolean;
}

export interface PendingAction {
  type: 'REPORT_STATS' | 'LEAVE_REVIEWS';
  label: string;
  icon: string;
  count?: number;
}

export interface PendingActionMatch {
  matchId: string;
  location: string;
  date: string;
  time: string;
  finishedAt: string;
  expiresAt: string;
  actions: PendingAction[];
}

export interface NeedsFinalizationMatch {
  id: string;
  location: string;
  date: string;
  time: string;
}

export interface HomeFeedData {
  nextMatch: NextMatch | null;
  topMatches: AvailableMatch[];
  allMatches: AvailableMatch[];
  availableMatches: AvailableMatch[];
  friendActivity: unknown[];
  pendingActions?: PendingActionMatch[];
  needsFinalization?: NeedsFinalizationMatch | null;
}

const CACHE_KEY = 'home_feed_cache';
const CACHE_TIMESTAMP_KEY = 'home_feed_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function getCacheTimestamp(): number | null {
  try {
    const ts = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);
    return ts ? parseInt(ts, 10) : null;
  } catch {
    return null;
  }
}

function isCacheValid(): boolean {
  const ts = getCacheTimestamp();
  if (!ts) return false;
  return Date.now() - ts < CACHE_DURATION;
}

function loadCachedData(): HomeFeedData | null {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

function saveCache(data: HomeFeedData): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
    sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch {
    // ignore
  }
}

export function useHomeFeed() {
  const [data, setData] = useState<HomeFeedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFeed = useCallback(async (options: { bustCache?: boolean } = {}) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoading(false);
      setError('No hay sesión');
      return;
    }

    if (!options.bustCache && isCacheValid()) {
      const cached = loadCachedData();
      if (cached) {
        setData(cached);
        setLoading(false);
        setError(null);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(getApiUrl('/api/home/feed'), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `Error ${res.status}`);
      }

      const feedData: HomeFeedData = await res.json();
      setData(feedData);
      saveCache(feedData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al cargar el home';
      setError(message);
      const cached = loadCachedData();
      if (cached) setData(cached);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  // Al volver a la pestaña/ventana, refrescar si la caché tiene más de 30s (para ver próximo partido tras unirse)
  useEffect(() => {
    const onFocus = () => {
      const ts = getCacheTimestamp();
      if (!ts || Date.now() - ts > 30 * 1000) fetchFeed({ bustCache: true });
    };
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('focus', onFocus);
      return () => window.removeEventListener('focus', onFocus);
    }
  }, [fetchFeed]);

  const refresh = useCallback(() => fetchFeed({ bustCache: true }), [fetchFeed]);

  return { data, loading, error, refresh };
}
